import { useEffect, useState } from 'react';

import { useTheme } from './theme';

/**
 * Colores de los gráficos leídos de los tokens (`--chart-*` en packages/ui). ECharts pinta en canvas y
 * no entiende `var()`, así que se resuelven a hex al cambiar de tema. Paleta validada con el
 * validador de dataviz (banda de luminosidad, croma, separación para daltonismo, contraste 3:1).
 */
export interface ChartColors {
  series: [string, string, string, string];
  grid: string;
  axis: string;
  text: string;
  surface: string;
}

const FALLBACK: Record<'light' | 'dark', ChartColors> = {
  light: {
    series: ['#0891b2', '#2f9e44', '#7048e8', '#d9480f'],
    grid: '#e3e9f0',
    axis: '#5d6b7e',
    text: '#0d1b2e',
    surface: '#ffffff',
  },
  dark: {
    series: ['#0aa5c0', '#2fa84f', '#8b6cf0', '#e8590c'],
    grid: '#1a3152',
    axis: '#93a8c4',
    text: '#e6eef8',
    surface: '#0c1e36',
  },
};

function read(theme: 'light' | 'dark'): ChartColors {
  if (typeof document === 'undefined') return FALLBACK[theme];
  const css = getComputedStyle(document.documentElement);
  const v = (name: string, fb: string) => css.getPropertyValue(name).trim() || fb;
  const fb = FALLBACK[theme];
  return {
    series: [
      v('--chart-1', fb.series[0]),
      v('--chart-2', fb.series[1]),
      v('--chart-3', fb.series[2]),
      v('--chart-4', fb.series[3]),
    ],
    grid: v('--chart-grid', fb.grid),
    axis: v('--chart-axis', fb.axis),
    text: v('--foreground', fb.text),
    surface: v('--card', fb.surface),
  };
}

export function useChartColors(): { theme: 'light' | 'dark'; colors: ChartColors } {
  const { resolved } = useTheme();
  const [colors, setColors] = useState<ChartColors>(() => FALLBACK[resolved]);
  useEffect(() => {
    // La clase `dark` se aplica en un efecto del tema: se lee en el próximo frame.
    const id = requestAnimationFrame(() => {
      setColors(read(resolved));
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [resolved]);
  return { theme: resolved, colors };
}

const FONT = 'Open Sans Variable, Open Sans, system-ui, sans-serif';

/**
 * Base común: ejes y grilla recesivos (grilla punteada, sin ticks, etiquetas en tinta apagada),
 * tipografía de la consola y un tooltip con la misma piel que los menús (radio, sombra en capas).
 */
export function baseChart(c: ChartColors) {
  return {
    textStyle: { fontFamily: FONT },
    animationDuration: 500,
    animationEasing: 'cubicOut' as const,
    tooltip: {
      trigger: 'axis',
      backgroundColor: c.surface,
      borderColor: c.grid,
      borderWidth: 1,
      padding: [8, 12],
      textStyle: { color: c.text, fontFamily: FONT, fontSize: 12 },
      extraCssText:
        'border-radius:10px;box-shadow:0 4px 10px -2px rgba(10,35,66,.12),0 18px 40px -12px rgba(10,35,66,.25);',
      axisPointer: {
        type: 'shadow',
        shadowStyle: { color: c.grid, opacity: 0.45 },
        lineStyle: { color: c.axis, type: 'dashed' },
      },
    },
    xAxis: {
      type: 'category',
      axisLine: { lineStyle: { color: c.grid } },
      axisTick: { show: false },
      axisLabel: { color: c.axis, hideOverlap: true, fontSize: 11, margin: 10 },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitNumber: 4,
      splitLine: { lineStyle: { color: c.grid, type: [3, 4] } },
      axisLabel: { color: c.axis, fontSize: 11 },
    },
  };
}

/** Relleno vertical del color de la serie hacia transparente (áreas bajo una línea). */
export function areaFill(color: string, from = 0.22) {
  const alpha = (a: number) =>
    color.startsWith('#') && color.length === 7
      ? `${color}${Math.round(a * 255)
          .toString(16)
          .padStart(2, '0')}`
      : color;
  return {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: alpha(from) },
      { offset: 1, color: alpha(0) },
    ],
  };
}
